import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { checkSmsChallenge, confirmSmsReset } from "@/lib/api";
import { HttpError } from "@/lib/http";

const SOFT_MIN = 10; // backend enforces the authoritative role-aware policy

type Phase = "checking" | "valid" | "invalid" | "expired" | "used" | "locked" | "done";

function reasonToPhase(reason: string): Phase {
  if (reason === "expired") return "expired";
  if (reason === "used") return "used";
  if (reason === "too_many_attempts") return "locked";
  return "invalid";
}

/** Read the opaque challenge reference from the URL fragment (never sent to the
 *  server) and wipe it from the address bar immediately. */
function takeChallengeFromUrl(): string {
  let challenge = "";
  const m = /(?:^#|&)challenge=([^&]+)/.exec(window.location.hash || "");
  if (m) challenge = decodeURIComponent(m[1]);
  if (challenge) window.history.replaceState(null, "", window.location.pathname);
  return challenge;
}

export function ResetPasswordSmsPage() {
  const nav = useNavigate();
  const challengeRef = React.useRef<string | null>(null);
  if (challengeRef.current === null) challengeRef.current = takeChallengeFromUrl();
  const challenge = challengeRef.current;

  const [phase, setPhase] = React.useState<Phase>("checking");
  const [code, setCode] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    if (!challenge) { setPhase("invalid"); return; }
    checkSmsChallenge(challenge)
      .then((r) => { if (!cancelled) setPhase(r.valid ? "valid" : reasonToPhase(r.reason)); })
      .catch(() => { if (!cancelled) setPhase("invalid"); });
    return () => { cancelled = true; };
  }, [challenge]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await confirmSmsReset(challenge, code, pw);
      setPhase("done");
      toast.success("Password changed — you can sign in now");
      setTimeout(() => nav("/login", { replace: true }), 1200);
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as any;
        if (body?.error === "weak_password") {
          toast.error(body.message || "Password does not meet the requirements");
        } else if (body?.error === "invalid_code") {
          toast.error("That code is incorrect. Check the code and try again.");
        } else if (body?.error === "too_many_attempts") {
          setPhase("locked");
        } else if (body?.error === "invalid_challenge") {
          setPhase(reasonToPhase(body.reason || "invalid"));
        } else {
          toast.error(body?.message || err.message || "Reset failed");
        }
      } else {
        toast.error("Reset failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = code.trim().length === 6 && pw.length >= SOFT_MIN && pw === confirm && !submitting;

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Reset your password</CardTitle>
            <CardDescription>
              {phase === "valid"
                ? "Enter the code we texted you and choose a new password."
                : "Password reset"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {phase === "checking" && <div className="text-sm opacity-70">Checking your reset link…</div>}

            {(phase === "invalid" || phase === "expired" || phase === "used" || phase === "locked") && (
              <div className="space-y-4">
                <div className="text-sm text-red-600 dark:text-red-400">
                  {phase === "expired" ? "This reset code has expired."
                    : phase === "used" ? "This reset link has already been used."
                    : phase === "locked" ? "Too many incorrect attempts. Request a new reset."
                    : "This reset link is invalid."}
                </div>
                <div className="text-xs opacity-60">
                  Request a new password reset, then use the newest text message.
                </div>
                <Button className="w-full" variant="outline" onClick={() => nav("/login")}>
                  Back to sign in
                </Button>
              </div>
            )}

            {phase === "done" && (
              <div className="space-y-4">
                <div className="text-sm text-green-600 dark:text-green-400">Your password has been changed.</div>
                <Button className="w-full" onClick={() => nav("/login", { replace: true })}>Sign in</Button>
              </div>
            )}

            {phase === "valid" && (
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label>Verification code</Label>
                  <Input
                    className="mt-2 tracking-widest"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                  />
                </div>
                <div>
                  <Label>New password</Label>
                  <div className="relative mt-2">
                    <Input
                      className="pr-10"
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                    />
                    <button type="button" tabIndex={-1}
                      aria-label={show ? "Hide password" : "Show password"}
                      onClick={() => setShow((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="mt-1 text-xs opacity-60">{pw.length}/{SOFT_MIN}+ characters</div>
                </div>
                <div>
                  <Label>Confirm password</Label>
                  <Input className="mt-2" type={show ? "text" : "password"} autoComplete="new-password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                  {confirm.length > 0 && pw !== confirm && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords do not match</div>
                  )}
                </div>
                <Button className="w-full" type="submit" disabled={!canSubmit}>
                  {submitting ? "Saving…" : "Set new password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
